import { propSolid, type ArenaBox, type ArenaDefinition, type ArenaProp, type Vec3 } from "@arena/shared";

/**
 * L'ingresso dell'Inferno (docs/DECISIONS.md, D39; Inferno, canto III): a
 * plateau of burnt rock before the Gate ("Lasciate ogne speranza, voi
 * ch'intrate"), the Acheron along the north edge with Charon's pier, the
 * abyss everywhere else. Full of things: boulders to hide behind, broken
 * columns, braziers and dead trees to break, stones and skulls to throw.
 *
 * The plateau is a rounded rectangle cut in bands 0.5 m deep (the simulation
 * knows only boxes); falling off any edge is a ring-out.
 */
export const INFERNO_LAYOUT={a:19,b:11,p:3.5,eastFenceX:18.6} as const;
const A = INFERNO_LAYOUT.a;
const B = INFERNO_LAYOUT.b;
/** Squareness of the plateau: 2 = ellipse, higher = squarer with round corners. */
const P = 3.5;
const BAND = 0.5;

const r = (v: number) => Math.round(v * 1000) / 1000;
const halfWidth = (z: number) => A * Math.max(0, 1 - Math.abs(z / B) ** P) ** (1 / P);
/** Shared outline: rounded western shore, straight eastern stockade. */
export function infernoEdge(t:number):{x:number;z:number}{
  const c=Math.cos(t),s=Math.sin(t);
  const radius=1/(Math.abs(c/A)**P+Math.abs(s/B)**P)**(1/P);
  return {x:c>1e-6?A:c*radius,z:s*radius};
}

function plateau(): ArenaBox[] {
  const boxes: ArenaBox[] = [];
  for (let z0 = -B; z0 < B - 1e-6; z0 += BAND) {
    const z1 = z0 + BAND;
    const w = r(halfWidth((z0 + z1) / 2));
    if (w < 0.5) continue;
    boxes.push({ id: `rock-${boxes.length}`, kind: "floor", min: { x: -w, y: -1, z: r(z0) }, max: { x: A, y: 0, z: r(z1) } });
  }
  return boxes;
}

/** Charon's pier, out over the Acheron. */
const PIER: ArenaBox = { id: "pier", kind: "floor", min: { x: 4.5, y: -0.6, z: 10 }, max: { x: 7, y: 0, z: 15 } };
/** The lintel of the Gate: walk under it, jump onto it from the pillars. */
const LINTEL: ArenaBox = { id: "gate-lintel", kind: "wall", min: { x: -17.5, y: 6, z: -3.4 }, max: { x: -15.7, y: 7.3, z: 3.4 } };
const EAST_FENCE:ArenaBox={id:'east-stockade',kind:'wall',min:{x:18.4,y:0,z:-11.5},max:{x:19,y:12,z:11.5}};

/**
 * The mountain the Gate is cut into, on the west side: the Gate opens into a
 * short dark passage that leads down (drawn, not walkable). It also closes
 * the west edge: nobody falls off there.
 */
const MASSIF: ArenaBox[] = [
  { id: "massif-s", kind: "wall", min: { x: -22, y: -12, z: -12 }, max: { x: -17.3, y: 12, z: -3.3 } },
  { id: "massif-n", kind: "platform", min: { x: -22, y: -12, z: 3.3 }, max: { x: -17.3, y: 5, z: 12 } },
  { id: "massif-top", kind: "wall", min: { x: -22, y: 7.3, z: -3.3 }, max: { x: -17.5, y: 12, z: 3.3 } },
  { id: "massif-back", kind: "wall", min: { x: -22, y: -12, z: -3.3 }, max: { x: -18.6, y: 7.3, z: 3.3 } },
];

/** Broad landing shelves: every rise is reachable by the heaviest champion. */
const MOUNTAIN_STEPS:ArenaBox[]=[
  {id:'mountain-west-guard',kind:'wall',min:{x:-22.3,y:5,z:2.9},max:{x:-21.7,y:9,z:12.4}},
  {id:'mountain-step-1',kind:'platform',min:{x:-13.1,y:0,z:5.2},max:{x:-11.3,y:1.25,z:8.8}},
  {id:'mountain-step-2',kind:'platform',min:{x:-14.8,y:0,z:5.7},max:{x:-13.1,y:2.5,z:9.2}},
  {id:'mountain-step-3',kind:'platform',min:{x:-17.3,y:0,z:6.3},max:{x:-14.8,y:3.75,z:9.9}},
];

const size = (x: number, y: number, z: number): Vec3 => ({ x, y, z });

const prop = (id: string, kind: string, x: number, z: number, s: Vec3, o: Partial<ArenaProp> = {}): ArenaProp => ({
  id, kind, x, y: 0, z, size: s, blocks: true, hp: 0, carry: false, respawn: 20, ...o,
});
/** Something small to pick up and throw. */
const carry = (id: string, kind: string, x: number, z: number): ArenaProp => ({
  id, kind, x, y: 0, z, size: size(0.5, 0.45, 0.5), blocks: false, hp: 0, carry: true, respawn: 12,
});

const PROPS: ArenaProp[] = [
  // Large structures keep their ruins for the rest of the round.
  prop("cripta", "crypt", -11, -8.6, size(4.6, 3.5, 0.85), {hp:96,respawn:0}),
  prop("giudice", "statue", 13.4, 8.2, size(1.5, 4.2, 1.5), {
    hp:84,respawn:0,collapse:{warningTicks:48,length:4.4,width:1.8,damage:18,dx:-1,dz:-.7},
  }),
  prop("traghettatore", "shack", .3, 8.8, size(5.6, 3, 3.2), {hp:72,respawn:0}),
  prop("carico-tetto", "cargo", -1.4, 6.3, size(1.6, 1.5, 1.6), {hp:18,respawn:0}),
  prop("carico-molo", "cargo", 8.2, 9.5, size(1.4, 1.2, 1.2), {hp:18,respawn:0}),
  prop("carico-porta", "cargo", -15.5, -6.7, size(1.3, 1.1, 1.1), {hp:18,respawn:0}),
  // The Gate.
  prop("porta-s", "porta", -16.6, -2.6, size(1.4, 6, 1.4)),
  prop("porta-n", "porta", -16.6, 2.6, size(1.4, 6, 1.4)),
  // Boulders: cover, they never break.
  prop("masso-1", "masso", -14, -5.6, size(2, 1.8, 1.8)),
  prop("masso-2", "masso", 10.2, 9.3, size(1.6, 1.4, 1.6)),
  prop("masso-3", "masso", 16, -8.8, size(2.2, 2, 1.8)),
  prop("masso-4", "masso", -10.2, 9.3, size(1.8, 1.6, 1.6)),
  // The crag in the middle, to jump on.
  prop("rupe", "rupe", 15.2, -3, size(4, 1.2, 2.8)),
  // Ruined low walls, to jump on.
  prop("rovine-1", "rovine", -3.5, -9.7, size(3.2, 1, 0.8)),
  prop("rovine-2", "rovine", 17.1, 1.8, size(0.8, 1, 3)),
  // Columns survive repeated blows; their openings last until the next round.
  prop("colonna-1", "colonna", -14.6, 4.5, size(1, 3, 1), { hp: 24,respawn:0 }),
  prop("colonna-2", "colonna", -6, -8.8, size(1, 2.4, 1), { hp: 24,respawn:0 }),
  prop("colonna-3", "colonna", 6, -8.8, size(1, 3.2, 1), { hp: 24,respawn:0 }),
  prop("colonna-4", "colonna", 12, -8.8, size(1, 2, 1), { hp: 24,respawn:0 }),
  prop("colonna-5", "colonna", 0, -8.8, size(1, 2.7, 1), { hp: 24,respawn:0 }),
  // Braziers: two hits, and the fire goes out.
  prop("braciere-1", "braciere", -10.5, -3.8, size(0.8, 1.3, 0.8), { hp: 2, respawn: 25 }),
  prop("braciere-2", "braciere", -10.5, 3.8, size(0.8, 1.3, 0.8), { hp: 2, respawn: 25 }),
  prop("braciere-3", "braciere", 3.4, 6.1, size(0.8, 1.3, 0.8), { hp: 2, respawn: 25 }),
  prop("braciere-4", "braciere", 4, -7, size(0.8, 1.3, 0.8), { hp: 2, respawn: 25 }),
  prop("braciere-5", "braciere", 12, 1.5, size(0.8, 1.3, 0.8), { hp: 2, respawn: 25 }),
  // Dead trees of the wood of suicides: two hits.
  prop("albero-1", "albero", 11.5, -1.5, size(1, 3.5, 1), { hp: 2, respawn: 25 }),
  prop("albero-2", "albero", -4.5, 6.8, size(1, 3.5, 1), { hp: 2, respawn: 25 }),
  // Stones and skulls to throw.
  carry("sasso-1", "sasso", -2, 2.5),
  carry("sasso-2", "sasso", 3, 4.2),
  carry("sasso-3", "sasso", -5, -1.5),
  carry("sasso-4", "sasso", 8, -1.5),
  carry("sasso-5", "sasso", -8.5, -6.5),
  carry("teschio-1", "teschio", 6, -6.5),
  carry("teschio-2", "teschio", -7.5, 0),
  carry("teschio-3", "teschio", 9.5, 3),
  carry("teschio-4", "teschio", -1, 5),
];

export const INFERNO_ARENA: ArenaDefinition = {
  id: "inferno",
  schemaVersion: 1,
  displayName: "L'ingresso dell'Inferno",
  theme: "inferno",
  killY: -12,
  camera: { yaw: 0, pitch: 0.9, distance: 12.5 },
  solids: [...plateau(), PIER, LINTEL, EAST_FENCE, ...MASSIF, ...MOUNTAIN_STEPS, ...PROPS.filter((p) => p.blocks).map(propSolid)],
  props: PROPS,
  spawnPoints: [
    // Opposite sides first. Every slot is fixed, on the permanent outer bank.
    { x: -16, y: 0, z: 0 },
    { x: 17.3, y: 0, z: -0.65 },
    { x: 0, y: 0, z: -10 },
    { x: -5.5, y: 0, z: 9.6 },
    { x: -12.5, y: 0, z: -9.8 },
    { x: 13, y: 0, z: 9.8 },
    { x: -12.5, y: 0, z: 9.8 },
    { x: 12, y: 0, z: -10 },
  ],
  // Blue on the south side, Red on the north.
  teamSpawns: [
    [
      { x: -12.5, y: 0, z: -9.8 },
      { x: -6.5, y: 0, z: -10 },
      { x: 4.5, y: 0, z: -10 },
      { x: 12, y: 0, z: -10 },
    ],
    [
      { x: -12.5, y: 0, z: 9.8 },
      { x: -5.5, y: 0, z: 9.6 },
      { x: 4.5, y: 0, z: 9.8 },
      { x: 13, y: 0, z: 9.8 },
    ],
  ],
};
